import React, { useEffect, useState } from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';

import { createTanstackTable } from '../../../common/utils';
import { useGetOrgSharedRequests } from '../../../hooks/tanstack/useOrganizationHook';
import {
  useGetRouteParams,
  useGetSearchResults,
} from '../../../hooks/tanstack/useUtilsHook';
import TanstackTable from '../../TanstackTable';

import MemberApprovalListDialog from './MemberApprovalListDialog';

export type DataRow = {
  id: number;
  name: string;
  project: string;
  requester: string;
  approvedReceivers: string;
  createdAt: string;
  action?: React.ReactNode;
};

const columnHelper = createColumnHelper<DataRow>();

const columns = [
  columnHelper.accessor('name', {
    cell: (info) => (
      <div className="group flex items-center gap-2 py-3 text-start">
        <p className="w-full">{info.getValue()}</p>
      </div>
    ),
    header: () => (
      <div className="items-centert flex gap-2">
        <p>Model Name</p>
      </div>
    ),
  }),
  columnHelper.accessor('project', {
    id: 'project',
    cell: (info) =>
      info.getValue() ? (
        <div className="w-full">{info.getValue()}</div>
      ) : (
        <div className="text-gray-400 w-full text-center">--</div>
      ),
    header: () => <div>Project</div>,
  }),
  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    cell: (info) =>
      info.getValue() ? (
        <div className="w-full">
          {format(info.getValue() as string, 'y/MM/dd h:mm a')}
        </div>
      ) : (
        <div className="text-gray-400 w-full text-center">--</div>
      ),
    header: () => <div>Date Requested</div>,
  }),
  columnHelper.accessor('approvedReceivers', {
    id: 'approvedReceivers',
    cell: (info) =>
      info.getValue() ? (
        <div className="w-full">{info.getValue()}</div>
      ) : (
        <div className="text-gray-400 w-full text-center">--</div>
      ),
    header: () => <div>No. of Receivers</div>,
  }),
  columnHelper.accessor('action', {
    id: 'action',
    cell: (info) => {
      const id = info.row.original.id;

      return (
        <div className="w-full">
          <MemberApprovalListDialog id={id} />
        </div>
      );
    },
    header: () => <div>Action</div>,
  }),
];

const SentRequestsTable = ({ searchKey }: { searchKey: string }) => {
  const [data, setData] = useState<DataRow[]>([]);

  const { orgId } = useGetRouteParams();

  const {
    data: sharedRequests,
    isLoading: isFetchingOrgSharedRequests,
    isFetching: isRefetchingOrgSharedRequests,
  } = useGetOrgSharedRequests(orgId ?? NaN, {
    enabled: !!orgId,
  });

  const searchResults = useGetSearchResults<DataRow>({
    searchValue: searchKey,
    data,
    filterFields: ['project', 'name', 'requester'],
  });

  useEffect(() => {
    if (sharedRequests) {
      setData(
        sharedRequests.map((r) => {
          const approvedReceivers = r.sharing_invitations.filter(
            (i) => i.approve_status === 'Approved'
          );

          return {
            id: r.id,
            name: r.model.name,
            project: r.project.name,
            approvedReceivers: `${approvedReceivers.length} of ${r.sharing_invitations.length}`,
            createdAt: format(r.updated_at, 'y/MM/dd h:mm a'),
          };
        }) as DataRow[]
      );
    }
  }, [sharedRequests]);

  const table = createTanstackTable<DataRow>({
    data: searchResults ?? data,
    columns,
    defaultSorting: {
      id: 'createdAt',
      desc: true,
    },
  });

  return (
    <TanstackTable
      table={table}
      isLoading={isFetchingOrgSharedRequests || isRefetchingOrgSharedRequests}
      emptyState={searchKey ? 'No results' : 'No sharing shared requests.'}
    />
  );
};

export default SentRequestsTable;
